import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Plus, Minus, PlayCircle, Search, X, Video, ExternalLink, Camera, Building2, Shield } from 'lucide-react';
import {
  fetchSites,
  isSiteCameraOnline,
  onlineSiteCameras,
  siteMapAlignedOperational,
  siteMapMarkerColor,
  type Site,
  type SiteCamera,
} from '../data/sites';
import MapNotificationPanel, { type NotificationItem } from './MapNotificationPanel';
import { useAuth } from '../contexts/AuthContext';
import { useButtonAlertsPendingSync } from '../contexts/ButtonAlertsPendingSyncContext';
import { shouldShowMapNotificationOnWeb } from '../utils/buttonAlertSiteLink';
import { DEFAULT_MAP_INITIAL_VIEW, getMapInitialView } from '../utils/mapInitialView';
import './MapComponent.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

const MAX_CAMERAS_IN_GEOFENCE = 16;

const DEFAULT_CENTER: [number, number] = [DEFAULT_MAP_INITIAL_VIEW.lat, DEFAULT_MAP_INITIAL_VIEW.lng];
const DEFAULT_ZOOM = DEFAULT_MAP_INITIAL_VIEW.zoom;
/** Las cámaras solo se muestran en el mapa a partir de este nivel de zoom */
const MIN_ZOOM_FOR_CAMERAS = 15;

/**
 * Iconos por tipo de servicio:
 * - PVVC = policía / agente de seguridad
 * - EU (Escudo Urbano) = escudo
 * - Privado = cámara fija (video)
 */
type ServiceIconType = 'security' | 'shield' | 'camera';

function getServiceIconType(serviceName: string): ServiceIconType {
  const lower = (serviceName || '').toLowerCase();
  if (lower.includes('escudo') || lower.includes('urbano') || lower.includes('eu')) return 'shield';
  if (lower.includes('pvvc')) return 'security';
  if (lower.includes('privado')) return 'camera';
  return 'camera';
}

/** Categoría de servicio para disponibilidad: PVVC, PRIVADOS, EU */
export type ServiceCategory = 'pvvc' | 'privados' | 'eu';

function getServiceCategory(serviceName: string): ServiceCategory | null {
  const lower = (serviceName || '').toLowerCase();
  if (lower.includes('pvvc')) return 'pvvc';
  if (lower.includes('escudo') || lower.includes('urbano') || lower.includes('eu')) return 'eu';
  if (lower.includes('privado')) return 'privados';
  return null;
}

interface AvailabilityStats {
  total: number;
  online: number;
  partial: number;
  maintenance: number;
  offline: number;
  pct: number;
}

/** SVG: agente seguridad (PVVC), escudo (EU), cámara fija (Privado) */
function getIconSvg(type: ServiceIconType): string {
  switch (type) {
    case 'security':
      /* PVVC: policía / agente de seguridad (figura con gorra) */
      return '<svg width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="7" r="3.2"/><path d="M8 11h8v10H8z"/><path d="M12 2L9 6h6L12 2z"/><ellipse cx="12" cy="21" rx="4" ry="1.5"/></svg>';
    case 'shield':
      return '<svg width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>';
    case 'camera':
    default:
      /* Privado / marcador cámara: cámara fija (video) */
      return '<svg width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2" fill="white"/></svg>';
  }
}

interface MapComponentProps {
  onSiteClick?: (site: Site) => void;
}

function openCameraStreamInNewTab(cameraId: number) {
  const params = new URLSearchParams();
  params.set('cameraIds', String(cameraId));
  window.open(`/streams?${params.toString()}`, '_blank');
}

const SiteCameraPill: React.FC<{ cam: SiteCamera }> = ({ cam }) => {
  const name = cam.name?.trim() || 'Cámara';
  if (isSiteCameraOnline(cam)) {
    return (
      <button
        type="button"
        className="site-popup-camera-pill site-popup-camera-pill--link site-popup-camera-pill--online"
        onClick={() => openCameraStreamInNewTab(cam.id)}
        title={`Ver stream: ${name}`}
      >
        <span className="site-popup-camera-pill-dot" />
        <span className="site-popup-camera-pill-name">{name}</span>
        <PlayCircle size={14} className="site-popup-camera-pill-icon" />
      </button>
    );
  }
  const offline = cam.state === 0;
  return (
    <button
      type="button"
      disabled
      className={`site-popup-camera-pill site-popup-camera-pill--offline${offline ? '' : ' site-popup-camera-pill--unknown'}`}
      title={offline ? `${name} — Fuera de servicio` : `${name} — Sin dato de estado`}
    >
      <span className="site-popup-camera-pill-dot" />
      <span className="site-popup-camera-pill-name">{name}</span>
    </button>
  );
};

const SiteCamerasGrid: React.FC<{ cameras: SiteCamera[]; showActiveCount?: boolean }> = ({
  cameras,
  showActiveCount = false,
}) => {
  const list = cameras.filter((c) => c.id);
  if (list.length === 0) return null;
  const onlineCount = list.filter(isSiteCameraOnline).length;
  return (
    <div className="site-popup-cameras site-popup-cameras--card">
      <h4 className="site-popup-cameras-title">
        <Video size={16} className="site-popup-cameras-icon" />
        {showActiveCount
          ? `Estado de cámaras (${onlineCount}/${list.length} activas)`
          : `Cámaras del sitio (${onlineCount}/${list.length} online)`}
      </h4>
      <div className="site-popup-cameras-grid">
        {list.map((cam) => (
          <SiteCameraPill key={cam.id} cam={cam} />
        ))}
      </div>
    </div>
  );
};

/** Navegación desde otras páginas (p. ej. «Ir al punto» en el toast global). */
export interface MapPageFocusAlertState {
  focusMapAlert?: boolean;
  latitude?: number;
  longitude?: number;
  siteId?: number;
  siteName?: string;
}

/** Comprueba si un punto está dentro de un polígono (ray casting) */
function pointInPolygon(latLng: L.LatLng, polygon: L.Polygon): boolean {
  const latlngs = polygon.getLatLngs();
  const ring = Array.isArray(latlngs[0]) ? (latlngs[0] as L.LatLng[]) : (latlngs as L.LatLng[]);
  if (ring.length < 3) return false;
  const lat = latLng.lat;
  const lng = latLng.lng;
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i].lng, yi = ring[i].lat;
    const xj = ring[j].lng, yj = ring[j].lat;
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

/** Comprueba si un punto está dentro del layer (polígono o círculo) */
function pointInLayer(latLng: L.LatLng, layer: L.Layer): boolean {
  if (layer instanceof L.Polygon) return pointInPolygon(latLng, layer);
  if (layer instanceof L.Circle) {
    const center = layer.getLatLng();
    const radiusM = layer.getRadius();
    return center.distanceTo(latLng) <= radiusM;
  }
  return false;
}

/**
 * Obtiene las cámaras que están dentro del layer (polígono o círculo), usando solo sitios filtrados
 * (respeta PVVC/Privado/EU y búsqueda). Máximo MAX_CAMERAS_IN_GEOFENCE (16) para streaming en simultáneo.
 */
function getCamerasInLayer(layer: L.Layer, sites: Site[]): { count: number; cameraIds: number[] } {
  const cameraIds: number[] = [];
  for (const site of sites) {
    const cameras = site.cameras || [];
    for (const cam of cameras) {
      if (cam.latitude == null || cam.longitude == null || isNaN(cam.latitude) || isNaN(cam.longitude)) continue;
      const latLng = L.latLng(cam.latitude, cam.longitude);
      if (!pointInLayer(latLng, layer)) continue;
      cameraIds.push(cam.id);
      if (cameraIds.length >= MAX_CAMERAS_IN_GEOFENCE) return { count: cameraIds.length, cameraIds };
    }
  }
  return { count: cameraIds.length, cameraIds };
}

const MapComponent: React.FC<MapComponentProps> = ({ onSiteClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { notifyPendingButtonAlertsChanged } = useButtonAlertsPendingSync();
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const cameraMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const popupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMouseOverPopupRef = useRef(false);
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const filteredSitesRef = useRef<Site[]>([]);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  /** Cámara seleccionada en el mapa (solo visible con zoom >= MIN_ZOOM_FOR_CAMERAS) */
  const [selectedCamera, setSelectedCamera] = useState<{
    id: number;
    name: string;
    siteId: number;
    siteName: string;
    latitude: number;
    longitude: number;
  } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');
  const [visibleServiceTypes, setVisibleServiceTypes] = useState<Record<ServiceCategory, boolean>>({
    pvvc: true,
    privados: true,
    eu: true,
  });
  /** Punto de alerta en el mapa (notificación con scope 'map'); fly: si además hay que centrar el mapa */
  const [alertPoint, setAlertPoint] = useState<{ lat: number; lng: number; fly: boolean } | null>(null);
  const alertPulseMarkerRef = useRef<L.Marker | null>(null);
  /** Sitio asociado al pulso actual; al hacer clic en el pulso se abre el modal con este sitio */
  const alertSiteRef = useRef<Site | null>(null);
  /** Si true, el mapa se centra automáticamente al recibir una notificación de mapa; si false, no (para revisar el mapa sin saltos) */
  const [autoCenterOnMapNotification, setAutoCenterOnMapNotification] = useState(true);
  /** Sitio a mostrar en el modal de notificación push (alertas de mapa); null = modal cerrado */
  const [notificationModalSite, setNotificationModalSite] = useState<Site | null>(null);
  const lastMapNotificationRef = useRef<NotificationItem | null>(null);
  const [notificationModalExtra, setNotificationModalExtra] = useState<{
    titleLabel: string;
    buttonAlertId?: number;
  } | null>(null);
  const [managingModalAlarm, setManagingModalAlarm] = useState(false);

  // Estadísticas de disponibilidad por categoría (PVVC, PRIVADOS, EU)
  const availabilityStats = useMemo((): Record<ServiceCategory, AvailabilityStats> => {
    const empty: AvailabilityStats = { total: 0, online: 0, partial: 0, maintenance: 0, offline: 0, pct: 0 };
    const result: Record<ServiceCategory, AvailabilityStats> = {
      pvvc: { ...empty },
      privados: { ...empty },
      eu: { ...empty },
    };
    for (const site of sites) {
      const cat = getServiceCategory(site.serviceName);
      if (cat == null) continue;
      const s = result[cat];
      s.total++;
      const aligned = siteMapAlignedOperational(site);
      if (aligned === 'on') s.online++;
      else if (aligned === 'issue') s.partial++;
      else if (aligned === 'maint') s.maintenance++;
      else s.offline++;
    }
    for (const cat of ['pvvc', 'privados', 'eu'] as ServiceCategory[]) {
      const s = result[cat];
      s.pct = s.total > 0 ? Math.round((s.online / s.total) * 100) : 0;
    }
    return result;
  }, [sites]);

  const toggleServiceType = useCallback((cat: ServiceCategory) => {
    setVisibleServiceTypes(prev => ({ ...prev, [cat]: !prev[cat] }));
  }, []);

  // Tipos de servicio únicos para el filtro (como vmsfigma)
  const uniqueServiceNames = useMemo(() => {
    const set = new Set<string>();
    sites.forEach(s => {
      if (s.serviceName && s.serviceName !== 'Sin servicio') set.add(s.serviceName);
    });
    return Array.from(set).sort();
  }, [sites]);

  const normalizeSearch = useCallback((s: string) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''), []);

  // Sitios filtrados por búsqueda, tipo de servicio y visibilidad (PVVC/PRIVADOS/EU)
  const filteredSites = useMemo(() => {
    const anyVisible = visibleServiceTypes.pvvc || visibleServiceTypes.privados || visibleServiceTypes.eu;
    return sites.filter(site => {
      const cat = getServiceCategory(site.serviceName);
      // Si ningún proyecto está activo en «Estado de sitios», no debe mostrarse ningún sitio.
      if (!anyVisible) return false;
      if (cat != null && !visibleServiceTypes[cat]) return false;
      if (serviceTypeFilter !== 'all' && site.serviceName !== serviceTypeFilter) return false;
      if (!searchQuery.trim()) return true;
      const q = normalizeSearch(searchQuery);
      const matchText = (
        normalizeSearch(site.name).includes(q) ||
        normalizeSearch(site.address).includes(q) ||
        normalizeSearch(site.serviceName || '').includes(q) ||
        normalizeSearch(site.id.toString()).includes(q) ||
        (site.stateName && normalizeSearch(site.stateName).includes(q))
      );
      if (matchText) return true;
      // También buscar por nombre de cámara
      const cameras = site.cameras || [];
      for (const cam of cameras) {
        if (cam.name && normalizeSearch(cam.name).includes(q)) return true;
      }
      return false;
    });
  }, [sites, searchQuery, serviceTypeFilter, visibleServiceTypes, normalizeSearch]);

  // Referencias rápidas: localidades/estados presentes en los sitios (para chips)
  const searchReferences = useMemo(() => {
    const stateNames = new Set<string>();
    sites.forEach(s => {
      if (s.stateName && s.stateName.trim()) stateNames.add(s.stateName.trim());
    });
    return Array.from(stateNames).sort().slice(0, 8);
  }, [sites]);

  filteredSitesRef.current = filteredSites;

  // Cargar sitios al montar y refrescar periódicamente para colores de marcadores (estado / cámaras).
  useEffect(() => {
    void fetchSites().then(setSites);
    const intervalMs = 45_000;
    const id = window.setInterval(() => {
      void fetchSites().then(setSites);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, []);

  const clearPopup = useCallback(() => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
    setSelectedSite(null);
    setSelectedCamera(null);
    setPopupPosition(null);
    isMouseOverPopupRef.current = false;
  }, []);

  const updatePopupPosition = useCallback((lat: number, lng: number) => {
    if (!mapRef.current || !containerRef.current) return;
    const pt = mapRef.current.latLngToContainerPoint([lat, lng]);
    setPopupPosition({ x: pt.x, y: pt.y });
  }, []);

  const showPopup = useCallback((site: Site) => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
    setSelectedCamera(null);
    setSelectedSite(site);
    const map = mapRef.current;
    if (map) {
      map.panTo([site.latitude, site.longitude]);
      map.once('moveend', () => updatePopupPosition(site.latitude, site.longitude));
    } else {
      updatePopupPosition(site.latitude, site.longitude);
    }
  }, [updatePopupPosition]);

  const showCameraPopup = useCallback((camera: SiteCamera, site: Site) => {
    const lat = camera.latitude;
    const lng = camera.longitude;
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
    setSelectedSite(null);
    setSelectedCamera({
      id: camera.id,
      name: camera.name || 'Cámara',
      siteId: site.id,
      siteName: site.name,
      latitude: lat,
      longitude: lng,
    });
    const map = mapRef.current;
    if (map) {
      map.panTo([lat, lng]);
      map.once('moveend', () => updatePopupPosition(lat, lng));
    } else {
      updatePopupPosition(lat, lng);
    }
  }, [updatePopupPosition]);

  // Inicializar el mapa siempre en el primer render (el contenedor ya está en el DOM)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: false,
      zoomAnimation: true,
      fadeAnimation: true,
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Ajustar centro/zoom con lo que devuelva el backend (MAP_INITIAL_LAT/LNG/ZOOM);
    // se aplica solo si el usuario aún no interactuó con el mapa.
    let userInteracted = false;
    const onFirstUserMove = () => { userInteracted = true; };
    map.once('mousedown', onFirstUserMove);
    map.once('wheel', onFirstUserMove);
    map.once('touchstart', onFirstUserMove);
    getMapInitialView().then((view) => {
      if (userInteracted) return;
      map.setView([view.lat, view.lng], view.zoom);
      setZoomLevel(view.zoom);
    }).catch(() => { /* fallback ya aplicado */ });

    // Cerrar popup al hacer clic en el mapa (fuera del popup)
    map.on('click', () => {
      clearPopup();
    });

    // Capa para polígonos y geocercas (círculos)
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const onDrawCreated = (e: L.LeafletEvent) => {
      const layer = (e as L.DrawEvents.Created).layer;
      drawnItems.addLayer(layer);

      // Popup: al abrirlo recalculamos cámaras dentro del layer con los sitios filtrados actuales
      // (solo se cuentan cámaras de sitios visibles según PVVC/Privado/EU y búsqueda)
      layer.bindPopup(() => {
        const sitesList = filteredSitesRef.current;
        const { count, cameraIds } = getCamerasInLayer(layer, sitesList);
        const layerExt = layer as unknown as { _cameraIds?: number[]; _camerasCount?: number };
        layerExt._cameraIds = cameraIds;
        layerExt._camerasCount = count;

        const countText = count === 0
          ? 'No hay cámaras en esta zona'
          : count >= MAX_CAMERAS_IN_GEOFENCE
            ? `${MAX_CAMERAS_IN_GEOFENCE} cámaras seleccionadas (máximo)`
            : `${count} ${count === 1 ? 'cámara seleccionada' : 'cámaras seleccionadas'}`;

        const cameraIconSvg = '<svg class="geofence-popup-header-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>';

        const div = document.createElement('div');
        div.className = 'geofence-popup';
        div.innerHTML = `
          <div class="geofence-popup-header">
            ${cameraIconSvg}
            <span class="geofence-popup-count">${countText}</span>
          </div>
          <div class="geofence-popup-actions">
            <button type="button" class="geofence-popup-btn geofence-popup-btn--streaming" data-action="streaming" ${count === 0 ? 'disabled' : ''}>
              <span class="geofence-popup-btn-icon"></span> Ver Stream
            </button>
          </div>
        `;
        return div;
      }, { className: 'geofence-popup-container', minWidth: 200 });
    };

    map.on('draw:created', onDrawCreated);

    map.on('popupopen', (e: L.LeafletEvent) => {
      const popup = (e as L.PopupEvent).popup;
      const el = popup.getElement();
      if (!el) return;
      const streamingBtn = el.querySelector('[data-action="streaming"]');
      if (streamingBtn) {
        streamingBtn.addEventListener('click', () => {
          if ((streamingBtn as HTMLButtonElement).disabled) return;
          const sourceLayer = (popup as L.Popup & { _source?: L.Layer })._source;
          const layerExt = sourceLayer as unknown as { _cameraIds?: number[] };
          const cameraIds = layerExt?._cameraIds || [];
          const params = new URLSearchParams();
          if (cameraIds.length > 0) {
            params.set('cameraIds', cameraIds.join(','));
          }
          window.open(`/streams?${params.toString()}`, '_blank');
          map.closePopup();
        });
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const DrawControl = (L.Control as any).Draw;
    const drawControl = new DrawControl({
      position: 'topleft',
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: { color: '#3388ff', weight: 2 },
          metric: true,
        },
        circle: {
          shapeOptions: { color: '#3388ff', weight: 2 },
          metric: true,
        },
        polyline: false,
        rectangle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    });
    map.addControl(drawControl);
    drawControlRef.current = drawControl;

    // Parche: evitar "Cannot read properties of null (reading 'handler')" al hacer clic en Save en modo Eliminar (leaflet-draw)
    const editToolbar = (drawControl as unknown as { _toolbars?: Record<string, { _save?: () => void; _clearAllLayers?: () => void; _activeMode?: { handler: unknown } | null }> })._toolbars?.edit;
    if (editToolbar) {
      const origSave = editToolbar._save?.bind(editToolbar);
      if (origSave) {
        editToolbar._save = function (this: typeof editToolbar) {
          if (!this._activeMode || !this._activeMode.handler) return;
          origSave();
        };
      }
      const origClearAll = editToolbar._clearAllLayers?.bind(editToolbar);
      if (origClearAll) {
        editToolbar._clearAllLayers = function (this: typeof editToolbar) {
          if (!this._activeMode || !this._activeMode.handler) return;
          origClearAll();
        };
      }
    }

    map.on('zoomend', () => {
      setZoomLevel(map.getZoom());
    });
    setZoomLevel(map.getZoom());

    mapRef.current = map;
    setTimeout(() => mapRef.current?.invalidateSize(), 100);

    return () => {
      if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
      map.off('draw:created', onDrawCreated);
      map.off('zoomend');
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
      if (drawnItemsRef.current) {
        map.removeLayer(drawnItemsRef.current);
        drawnItemsRef.current = null;
      }
      markersRef.current.forEach(m => m.remove());
      markersRef.current.clear();
      cameraMarkersRef.current.forEach(m => m.remove());
      cameraMarkersRef.current.clear();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Actualizar posición del popup al mover el mapa (sitio o cámara)
  useEffect(() => {
    const target = selectedSite ?? selectedCamera;
    if (!mapRef.current || !target) return;
    const lat = target.latitude;
    const lng = target.longitude;
    const onMove = () => {
      if (target && !isMouseOverPopupRef.current) updatePopupPosition(lat, lng);
    };
    mapRef.current.on('move', onMove);
    mapRef.current.on('zoomend', onMove);
    return () => {
      mapRef.current?.off('move', onMove);
      mapRef.current?.off('zoomend', onMove);
    };
  }, [selectedSite, selectedCamera, updatePopupPosition]);

  // Mostrar efecto tipo sismo en punto de alerta; si fly, además centrar el mapa
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !alertPoint) return;

    const { lat, lng, fly } = alertPoint;
    if (fly) {
      map.flyTo([lat, lng], 16, { duration: 1.2 });
    }

    // Eliminar marcador de pulso anterior si existía
    if (alertPulseMarkerRef.current) {
      map.removeLayer(alertPulseMarkerRef.current);
      alertPulseMarkerRef.current = null;
    }

    const pulseIcon = L.divIcon({
      className: 'alert-pulse-wrapper alert-pulse-wrapper--clickable',
      html: `
        <div class="alert-pulse-epicenter"></div>
        <div class="alert-pulse-ring alert-pulse-ring--1"></div>
        <div class="alert-pulse-ring alert-pulse-ring--2"></div>
        <div class="alert-pulse-ring alert-pulse-ring--3"></div>
        <div class="alert-pulse-ring alert-pulse-ring--4"></div>
      `,
      iconSize: [80, 80],
      iconAnchor: [40, 40],
    });
    const pulseMarker = L.marker([lat, lng], { icon: pulseIcon, interactive: true })
      .addTo(map)
      .on('click', () => {
        if (alertSiteRef.current) {
          const n = lastMapNotificationRef.current;
          setNotificationModalExtra({
            titleLabel: n?.sourceType === 'iot' ? 'Alerta de botón' : 'Alerta',
            buttonAlertId: n?.buttonAlertId,
          });
          setNotificationModalSite(alertSiteRef.current);
        }
      });
    try {
      pulseMarker.bindTooltip('Clic para ver sitio y cámaras', {
        permanent: false,
        direction: 'top',
        offset: [0, -44],
      });
    } catch {
      // ignore si bindTooltip no está disponible
    }
    alertPulseMarkerRef.current = pulseMarker;

    const timeout = setTimeout(() => {
      if (alertPulseMarkerRef.current && mapRef.current) {
        mapRef.current.removeLayer(alertPulseMarkerRef.current);
        alertPulseMarkerRef.current = null;
      }
      alertSiteRef.current = null;
      setAlertPoint(null);
    }, 6000);

    return () => {
      clearTimeout(timeout);
      if (alertPulseMarkerRef.current && mapRef.current) {
        mapRef.current.removeLayer(alertPulseMarkerRef.current);
        alertPulseMarkerRef.current = null;
      }
      alertSiteRef.current = null;
    };
  }, [alertPoint]);

  // Georreferenciar cada sitio: iconos fijos 24px con borde blanco (como vmsfigma: 28px)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sites.length) return;

    const iconSize = 24;

    const createIcon = (color: string, serviceName: string) => {
      const type = getServiceIconType(serviceName);
      const iconSvg = getIconSvg(type);
      const half = iconSize / 2;
      return L.divIcon({
        className: 'custom-marker',
        html: `<div class="marker-icon" style="width:${iconSize}px;height:${iconSize}px;background-color:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform 0.2s;">${iconSvg}</div>`,
        iconSize: [iconSize, iconSize],
        iconAnchor: [half, half],
      });
    };

    const ids = new Set(filteredSites.map(s => s.id.toString()));
    markersRef.current.forEach((m, id) => {
      if (!ids.has(id)) {
        m.remove();
        markersRef.current.delete(id);
      }
    });

    filteredSites.forEach(site => {
      const lat = site.latitude;
      const lng = site.longitude;
      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;

      const color = siteMapMarkerColor(site);
      const siteId = site.id.toString();
      const icon = createIcon(color, site.serviceName);

      const existing = markersRef.current.get(siteId);
      if (existing) {
        existing.setIcon(icon);
        existing.setLatLng([lat, lng]);
        return;
      }

      const marker = L.marker([lat, lng], {
        icon,
        riseOnHover: true,
      }).addTo(map);

      marker.on('mouseover', (e) => {
        const iconEl = e.target.getElement()?.querySelector('.marker-icon') as HTMLElement;
        if (iconEl) iconEl.style.transform = 'scale(1.3)';
      });
      marker.on('mouseout', (e) => {
        const iconEl = e.target.getElement()?.querySelector('.marker-icon') as HTMLElement;
        if (iconEl) iconEl.style.transform = 'scale(1)';
      });
      marker.on('click', () => showPopup(site));
      markersRef.current.set(siteId, marker);
    });
  }, [filteredSites, showPopup, clearPopup]);

  // Marcadores de cámaras: solo visibles con zoom >= MIN_ZOOM_FOR_CAMERAS; mismo tamaño que sitios (24px)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const iconSize = 24;
    const cameraIconSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2" fill="white"/></svg>';
    const half = iconSize / 2;
    const cameraMarkerColor = (cam: SiteCamera): string => {
      if (cam.state === 0) return '#dc2626';
      if (cam.state === 1) return '#22c55e';
      return '#6366f1';
    };
    const createCameraIcon = (color: string) =>
      L.divIcon({
        className: 'custom-marker camera-marker',
        html: `<div class="marker-icon" style="width:${iconSize}px;height:${iconSize}px;background-color:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform 0.2s;">${cameraIconSvg}</div>`,
        iconSize: [iconSize, iconSize],
        iconAnchor: [half, half],
      });

    if (zoomLevel < MIN_ZOOM_FOR_CAMERAS) {
      cameraMarkersRef.current.forEach(m => m.remove());
      cameraMarkersRef.current.clear();
      setSelectedCamera(null);
      return;
    }

    const expectedKeys = new Set<string>();
    filteredSites.forEach(site => {
      (site.cameras || []).forEach((cam: SiteCamera) => {
        const lat = cam.latitude;
        const lng = cam.longitude;
        if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
          expectedKeys.add(`camera_${cam.id}`);
        }
      });
    });

    cameraMarkersRef.current.forEach((m, key) => {
      if (!expectedKeys.has(key)) {
        m.remove();
        cameraMarkersRef.current.delete(key);
      }
    });

    filteredSites.forEach(site => {
      (site.cameras || []).forEach((cam: SiteCamera) => {
        const lat = cam.latitude;
        const lng = cam.longitude;
        if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;
        const key = `camera_${cam.id}`;
        const camColor = cameraMarkerColor(cam);
        const icon = createCameraIcon(camColor);
        const existing = cameraMarkersRef.current.get(key);
        if (existing) {
          existing.setIcon(icon);
          existing.setLatLng([lat, lng]);
          return;
        }

        const marker = L.marker([lat, lng], {
          icon,
          riseOnHover: true,
        }).addTo(map);

        marker.on('mouseover', (e: L.LeafletMouseEvent) => {
          const iconEl = (e.target as L.Marker).getElement()?.querySelector('.marker-icon') as HTMLElement;
          if (iconEl) iconEl.style.transform = 'scale(1.3)';
        });
        marker.on('mouseout', (e: L.LeafletMouseEvent) => {
          const iconEl = (e.target as L.Marker).getElement()?.querySelector('.marker-icon') as HTMLElement;
          if (iconEl) iconEl.style.transform = 'scale(1)';
        });
        marker.on('click', () => showCameraPopup(cam, site));
        cameraMarkersRef.current.set(key, marker);
      });
    });
  }, [filteredSites, zoomLevel, showCameraPopup]);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleMouseLeave = () => {
    popupTimeoutRef.current = setTimeout(() => {
      if (!isMouseOverPopupRef.current) clearPopup();
    }, 200);
  };
  const handlePopupMouseEnter = () => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
    isMouseOverPopupRef.current = true;
  };
  const handlePopupMouseLeave = () => {
    isMouseOverPopupRef.current = false;
    popupTimeoutRef.current = setTimeout(clearPopup, 200);
  };

  /** Grupos de sitios para Estado de sitios (estilo imagen 1: lista con icono y count) */
  const siteGroupsConfig: { key: ServiceCategory; label: string; Icon: React.ElementType; iconColor: string }[] = [
    { key: 'pvvc', label: 'PVVC (Público)', Icon: Camera, iconColor: '#3b82f6' },
    { key: 'privados', label: 'Privado', Icon: Building2, iconColor: '#f97316' },
    { key: 'eu', label: 'EU', Icon: Shield, iconColor: '#E07054' },
  ];

  const showAllGroups = useCallback(() => {
    setVisibleServiceTypes({ pvvc: true, privados: true, eu: true });
  }, []);

  /** Al recibir notificación push: centrar mapa en el punto, mostrar pulso y guardar sitio para abrir modal al hacer clic en el pulso */
  const handleMapNotificationReceived = useCallback((notification: NotificationItem) => {
    if (!shouldShowMapNotificationOnWeb(notification)) return;
    lastMapNotificationRef.current = notification;
    const list = filteredSitesRef.current.length > 0 ? filteredSitesRef.current : sites;
    let site: Site | undefined;
    if (notification.siteId != null) {
      site = list.find((s) => s.id === Number(notification.siteId));
    }
    if (!site && notification.siteName) {
      const name = String(notification.siteName).trim();
      site = list.find((s) => (s.name || '').trim() === name);
    }
    if (!site && typeof notification.latitude === 'number' && typeof notification.longitude === 'number' && list.length > 0) {
      const latLng = L.latLng(notification.latitude, notification.longitude);
      let minDist = Infinity;
      for (const s of list) {
        const d = latLng.distanceTo(L.latLng(s.latitude, s.longitude));
        if (d < minDist) {
          minDist = d;
          site = s;
        }
      }
    }
    if (site) alertSiteRef.current = site;
    if (typeof notification.latitude === 'number' && typeof notification.longitude === 'number') {
      setAlertPoint({ lat: notification.latitude, lng: notification.longitude, fly: autoCenterOnMapNotification });
    }
  }, [sites, autoCenterOnMapNotification]);

  /** Enfocar alerta al llegar desde el toast global (fuera de /map). */
  useEffect(() => {
    const st = location.state as MapPageFocusAlertState | null;
    if (!st?.focusMapAlert) return;
    if (sites.length === 0) return;

    const item: NotificationItem = {
      id: Date.now(),
      receivedAt: Date.now(),
      scope: 'map',
      siteId: st.siteId != null ? Number(st.siteId) : undefined,
      siteName: typeof st.siteName === 'string' ? st.siteName : undefined,
      latitude: typeof st.latitude === 'number' ? st.latitude : undefined,
      longitude: typeof st.longitude === 'number' ? st.longitude : undefined,
    };
    handleMapNotificationReceived(item);
    navigate('.', { replace: true, state: {} });
  }, [location.state, sites.length, handleMapNotificationReceived, navigate]);

  const closeNotificationModal = useCallback(() => {
    setNotificationModalSite(null);
    setNotificationModalExtra(null);
  }, []);

  return (
    <div className="map-container" onMouseLeave={handleMouseLeave}>
      <div ref={containerRef} className="map-leaflet-container" />

      {selectedCamera && popupPosition && (
        <div
          className="site-popup site-popup--camera site-popup--card"
          style={{ left: `${popupPosition.x}px`, top: `${popupPosition.y - 10}px` }}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="site-popup-content">
            <div className="site-popup-header site-popup-header--card">
              <div className="site-popup-indicator site-popup-indicator--camera" style={{ backgroundColor: '#22c55e' }} />
              <span className="site-popup-type site-popup-type--card">Cámara</span>
              <button type="button" className="site-popup-close" onClick={clearPopup} title="Cerrar">
                <X size={18} />
              </button>
            </div>
            <div className="site-popup-details">
              <div className="site-popup-detail-row">
                <span className="site-popup-detail-label">Dispositivo</span>
                <span className="site-popup-detail-value">{selectedCamera.name}</span>
              </div>
              <div className="site-popup-detail-row">
                <span className="site-popup-detail-label">Sitio</span>
                <span className="site-popup-detail-value">{selectedCamera.siteName}</span>
              </div>
            </div>
            <div className="site-popup-actions site-popup-actions--card">
              <button
                type="button"
                className="site-popup-btn site-popup-btn--recordings"
                onClick={() => {
                  navigateRef.current?.('/recordings', { state: { siteId: selectedCamera.siteId, fromMap: true } });
                  clearPopup();
                }}
              >
                <Video size={18} />
                Ver grabaciones
              </button>
              <button
                type="button"
                className="site-popup-btn site-popup-btn--streaming"
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set('cameraIds', String(selectedCamera.id));
                  window.open(`/streams?${params.toString()}`, '_blank');
                  clearPopup();
                }}
              >
                <PlayCircle size={18} />
                Ver stream
              </button>
            </div>
            <div className="site-popup-arrow">
              <div style={{
                width: 0, height: 0,
                borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
                borderTop: '8px solid white',
              }} />
            </div>
          </div>
        </div>
      )}

      {selectedSite && popupPosition && (
        <div
          className="site-popup site-popup--card"
          style={{ left: `${popupPosition.x}px`, top: `${popupPosition.y - 10}px` }}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="site-popup-content">
            <div className="site-popup-header site-popup-header--card">
              <div
                className="site-popup-indicator"
                style={{ backgroundColor: siteMapMarkerColor(selectedSite) }}
              />
              <span className="site-popup-type site-popup-type--card">{selectedSite.serviceName || 'Sitio'}</span>
              <button
                type="button"
                className="site-popup-close"
                onClick={clearPopup}
                title="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="site-popup-details">
              <div className="site-popup-detail-row">
                <span className="site-popup-detail-label">Sitio</span>
                <span className="site-popup-detail-value">{selectedSite.name}</span>
              </div>
              <div className="site-popup-detail-row">
                <span className="site-popup-detail-label">Dirección</span>
                <span className="site-popup-detail-value">{selectedSite.address || '—'}</span>
              </div>
              {selectedSite.stateName && (
                <div className="site-popup-detail-row">
                  <span className="site-popup-detail-label">Localidad</span>
                  <span className="site-popup-detail-value">{selectedSite.stateName}</span>
                </div>
              )}
            </div>
            {selectedSite.cameras && selectedSite.cameras.length > 0 && (
              <SiteCamerasGrid cameras={selectedSite.cameras} showActiveCount />
            )}
            <div className="site-popup-actions site-popup-actions--card">
              <button
                type="button"
                className="site-popup-btn site-popup-btn--recordings"
                onClick={() => {
                  navigateRef.current?.('/recordings', { state: { siteId: selectedSite.id, fromMap: true } });
                  clearPopup();
                }}
              >
                <Video size={18} />
                Ver grabaciones
              </button>
              <button
                type="button"
                className="site-popup-btn site-popup-btn--streaming"
                disabled={onlineSiteCameras(selectedSite.cameras).length === 0}
                onClick={() => {
                  const cameraIds = onlineSiteCameras(selectedSite.cameras).map((c) => c.id);
                  const params = new URLSearchParams();
                  params.set('siteId', String(selectedSite.id));
                  params.set('siteName', selectedSite.name);
                  if (cameraIds.length > 0) {
                    params.set('cameraIds', cameraIds.join(','));
                  }
                  window.open(`/streams?${params.toString()}`, '_blank');
                  clearPopup();
                }}
                title={
                  onlineSiteCameras(selectedSite.cameras).length === 0
                    ? 'No hay cámaras online en este sitio'
                    : 'Ver stream de cámaras online'
                }
              >
                <PlayCircle size={18} />
                Ver stream
              </button>
            </div>
            <div className="site-popup-arrow">
              <div style={{
                width: 0, height: 0,
                borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
                borderTop: '8px solid white',
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal de notificación push: sitio y cámaras asociadas (estilo similar al popup de sitio) */}
      {notificationModalSite && (
        <div className="map-notification-modal-backdrop" onClick={closeNotificationModal} role="presentation">
          <div
            className="map-notification-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="map-notification-modal-title"
          >
            <div className="map-notification-modal-content">
              <div className="site-popup-header site-popup-header--card">
                <div
                  className="site-popup-indicator"
                  style={{ backgroundColor: siteMapMarkerColor(notificationModalSite) }}
                />
                <span id="map-notification-modal-title" className="site-popup-type site-popup-type--card">
                  {notificationModalExtra?.titleLabel ?? 'Alerta'} — {notificationModalSite.serviceName || 'Sitio'}
                </span>
                <button
                  type="button"
                  className="site-popup-close"
                  onClick={closeNotificationModal}
                  title="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="site-popup-details">
                <div className="site-popup-detail-row">
                  <span className="site-popup-detail-label">Sitio</span>
                  <span className="site-popup-detail-value">{notificationModalSite.name}</span>
                </div>
                <div className="site-popup-detail-row">
                  <span className="site-popup-detail-label">Dirección</span>
                  <span className="site-popup-detail-value">{notificationModalSite.address || '—'}</span>
                </div>
                {notificationModalSite.stateName && (
                  <div className="site-popup-detail-row">
                    <span className="site-popup-detail-label">Localidad</span>
                    <span className="site-popup-detail-value">{notificationModalSite.stateName}</span>
                  </div>
                )}
              </div>
              {notificationModalSite.cameras && notificationModalSite.cameras.length > 0 && (
                <SiteCamerasGrid cameras={notificationModalSite.cameras} />
              )}
              {notificationModalExtra?.buttonAlertId != null && token && (
                <div className="map-notification-modal__manage-wrap">
                  <button
                    type="button"
                    className="site-popup-btn site-popup-btn--manage-alarm"
                    disabled={managingModalAlarm}
                    title="Marcar como gestionada"
                    onClick={() => {
                      const id = notificationModalExtra.buttonAlertId;
                      if (id == null || !token) return;
                      void (async () => {
                        setManagingModalAlarm(true);
                        try {
                          await axios.patch(
                            `${API_URL}/button-alerts/${id}/acknowledge`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success('Alarma gestionada');
                          notifyPendingButtonAlertsChanged();
                          closeNotificationModal();
                        } catch {
                          toast.error('No se pudo registrar la gestión');
                        } finally {
                          setManagingModalAlarm(false);
                        }
                      })();
                    }}
                  >
                    {managingModalAlarm ? '…' : 'Gestionar alarma'}
                  </button>
                </div>
              )}
              <div className="site-popup-actions site-popup-actions--card">
                <button
                  type="button"
                  className="site-popup-btn site-popup-btn--recordings"
                  onClick={() => {
                    navigateRef.current?.('/recordings', { state: { siteId: notificationModalSite.id, fromMap: true } });
                    closeNotificationModal();
                  }}
                >
                  <Video size={18} />
                  Ver grabaciones
                </button>
                <button
                  type="button"
                  className="site-popup-btn site-popup-btn--streaming"
                  disabled={onlineSiteCameras(notificationModalSite.cameras).length === 0}
                  onClick={() => {
                    const cameraIds = onlineSiteCameras(notificationModalSite.cameras).map((c) => c.id);
                    const params = new URLSearchParams();
                    params.set('siteId', String(notificationModalSite.id));
                    params.set('siteName', notificationModalSite.name);
                    if (cameraIds.length > 0) params.set('cameraIds', cameraIds.join(','));
                    window.open(`/streams?${params.toString()}`, '_blank');
                    closeNotificationModal();
                  }}
                  title={
                    onlineSiteCameras(notificationModalSite.cameras).length === 0
                      ? 'No hay cámaras online en este sitio'
                      : 'Ver stream de cámaras online'
                  }
                >
                  <PlayCircle size={18} />
                  Ver stream
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="map-controls">
        <button onClick={handleZoomIn} title="Acercar">
          <Plus size={20} className="map-controls-icon" />
        </button>
        <button onClick={handleZoomOut} title="Alejar">
          <Minus size={20} className="map-controls-icon" />
        </button>
        <button
          onClick={() => setShowSearch(!showSearch)}
          className={showSearch ? 'active' : ''}
          title="Buscar"
        >
          <Search size={20} className={showSearch ? 'map-controls-icon--active' : 'map-controls-icon'} />
        </button>
      </div>

      {/* Panel de búsqueda: texto + tipo de servicio; se combina con Estado de sitios */}
      {showSearch && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3>Buscador</h3>
            <button onClick={() => setShowSearch(false)} className="close-button" type="button" aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>

          <p className="search-panel-hint">
            La búsqueda se combina con el tipo de servicio y con los grupos del mapa (Estado de sitios).
          </p>

          <div className="search-panel-field">
            <label>Buscar por sitio, dirección, localidad o cámara</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Ej: Chapinero, Calle 26, Mosquera..."
              aria-label="Texto de búsqueda"
            />
          </div>

          {searchReferences.length > 0 && (
            <div className="search-panel-references">
              <span className="search-panel-references-label">Referencias:</span>
              <div className="search-panel-chips">
                {searchReferences.map((ref) => (
                  <button
                    key={ref}
                    type="button"
                    className="search-panel-chip"
                    onClick={() => setSearchQuery(ref)}
                  >
                    {ref}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="search-panel-field">
            <label>Tipo de servicio</label>
            <select
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value)}
              aria-label="Filtrar por tipo de servicio"
            >
              <option value="all">Todos</option>
              {uniqueServiceNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="search-panel-result-count">
            {filteredSites.length === 0
              ? 'Ningún resultado con estos filtros'
              : `${filteredSites.length} ${filteredSites.length === 1 ? 'sitio' : 'sitios'}`
            }
          </div>

          <div className="search-panel-actions">
            <button
              type="button"
              className="search-panel-clear"
              onClick={() => {
                setSearchQuery('');
                setServiceTypeFilter('all');
              }}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="search-panel-submit"
              onClick={() => setShowSearch(false)}
            >
              Aplicar y cerrar
            </button>
          </div>
        </div>
      )}

      {/* Estado de sitios: grupos (PVVC, Privado, EU) y Mostrar todos */}
      <div className="map-legend">
        <h3>Estado de sitios</h3>
        <div className="map-legend-groups">
          {siteGroupsConfig.map(({ key, label, Icon, iconColor }) => {
            const stats = availabilityStats[key];
            const isActive = visibleServiceTypes[key];
            return (
              <button
                key={key}
                type="button"
                className={`legend-group-item ${isActive ? 'active' : ''}`}
                onClick={() => toggleServiceType(key)}
                title={isActive ? `Ocultar ${label} en el mapa` : `Mostrar ${label} en el mapa`}
              >
                <span className="legend-group-icon" style={{ color: iconColor }}>
                  <Icon size={18} />
                </span>
                <span className="legend-group-label">{label}</span>
                <span className="legend-group-count">{stats.total}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="map-legend-show-all"
          onClick={showAllGroups}
        >
          Mostrar todos
        </button>
      </div>

      <MapNotificationPanel
        onMapNotificationReceived={handleMapNotificationReceived}
        onFocusOnMap={(lat, lng, fly = true) => setAlertPoint({ lat, lng, fly })}
        autoCenterOnMapNotification={autoCenterOnMapNotification}
        onAutoCenterOnMapNotificationChange={setAutoCenterOnMapNotification}
      />

      <div className="map-footer">
        © 2025 — Verytel{' '}
        <a href="https://grupoverytel.com" target="_blank" rel="noopener noreferrer">
          grupoverytel.com
        </a>
      </div>
    </div>
  );
};

export default MapComponent;
